import React from 'react';
import CTA from '../components/CTA';
import MiniContactForm from '../components/MiniContactForm';
import FAQ from '../components/FAQ';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';

const GutterCleaning = () => {
  const serviceSEO = {
    title: 'Gutter Cleaning Services | Copper Canyon Pressure Wash',
    description: 'Professional gutter cleaning in Phoenix, AZ. Remove debris, prevent water damage. Expert technicians, safe cleaning methods. Get your free quote today!'
  };
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Gutter Cleaning',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'Copper Canyon Pressure Wash'
    },
    'areaServed': {
      '@type': 'City',
      'name': ['Phoenix', 'Scottsdale', 'Mesa', 'Tempe', 'Gilbert', 'Paradise Valley']
    },
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Pressure Washing Services',
      'itemListElement': {
        '@type': 'Offer',
        'itemOffered': {
          '@type': 'Service',
          'name': 'Gutter Cleaning'
        }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <SEO 
        title="Gutter Cleaning Services | Copper Canyon Pressure Wash"
        description="Professional gutter cleaning in Phoenix, AZ. Remove debris & prevent clogs. Expert technicians, eco-friendly solutions. Get your free quote today!"
        schema={serviceSchema}
        keywords="roof gutter cleaning, downspout cleaning, gutter maintenance, Phoenix gutter service, gutter debris removal, gutter clog prevention, rain gutter cleaning, gutter inspection, gutter repair preparation, water damage prevention"
      />
      <section className="w-full h-48 sm:h-64 md:h-80 lg:h-96 mb-8 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center" aria-label="Gutter Cleaning Banner">
        <picture>
          <source srcSet="/gutters_hero.avif" type="image/avif" />
          <source srcSet="/gutters_hero.webp" type="image/webp" />
        <img 
          src="/gutters_hero.png" 
          alt="Professional gutter cleaning in action" 
          className="w-full h-full object-cover object-center" 
            width="1600"
            height="400"
        />
        </picture>
      </section>
      <div className="max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-[#4a3f35] mb-6">Gutter Cleaning</h1>
        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4">Professional Gutter Cleaning Services</h2>
        <p className="mb-6 text-gray-700">
          At Copper Canyon Pressure Wash, we offer professional gutter cleaning services for homeowners in the greater Phoenix area, based in Scottsdale. Gutters can easily become clogged with leaves, dirt, and debris from Arizona's dust storms and monsoon season, leading to water damage or pest issues. Our team uses safe, effective cleaning methods to clear your gutters, ensuring they function properly and protect your home from costly repairs.
        </p>
        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Overflow Prevention</h3>
            </div>
            <p className="text-gray-700">We remove debris that can cause overflows, preventing water damage to your home's foundation, roof, or siding.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Pest Risk Reduction</h3>
            </div>
            <p className="text-gray-700">Our cleaning reduces the risk of pest infestations, like mosquitoes or rodents, that thrive in clogged, damp gutters.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Gentle Pressure Washing</h3>
            </div>
            <p className="text-gray-700">We use gentle pressure washing to flush out fine dirt and grime, leaving your gutters clean without damaging their structure.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-[#b87c4c] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <h3 className="text-xl font-semibold text-[#4a3f35]">Curb Appeal & Value</h3>
            </div>
            <p className="text-gray-700">Clean gutters improve your home's curb appeal and help maintain its value, especially during Arizona's rainy seasons.</p>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Gutter Cleaning Process</h2>
        <div className="bg-[#f8f7f3] rounded-lg p-6 mb-8">
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Inspection</h3>
                <p className="text-gray-700">We begin by inspecting your gutters for blockages, such as leaves, twigs, or sediment buildup from desert storms.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Manual Debris Removal</h3>
                <p className="text-gray-700">We manually remove larger debris by hand or with specialized tools, ensuring nothing is pushed into downspouts.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Low-Pressure Flushing</h3>
                <p className="text-gray-700">Using low-pressure washing at 500 PSI, we flush out remaining dirt and check that water flows freely through the system.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-[#b87c4c] rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
              <div>
                <h3 className="text-lg font-semibold text-[#4a3f35] mb-2">Final Inspection</h3>
                <p className="text-gray-700">We finish with a final inspection, ensuring your gutters are clear and functioning to protect your home.</p>
              </div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-[#b87c4c] mb-4 mt-8">Our Expertise in Gutter Cleaning</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Pressure Settings</h3>
            <p className="text-gray-700">We use the right pressure settings—like 500 PSI for aluminum gutters—to avoid dents or seam separation.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">All Gutter Types</h3>
            <p className="text-gray-700">We're experienced with all gutter types, including seamless, sectional, and vinyl, ensuring each is cleaned safely.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Damage Prevention</h3>
            <p className="text-gray-700">Our expertise prevents mistakes like damaged downspouts or misaligned gutters, which can happen with inexperienced cleaners.</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-[#4a3f35] mb-3">Arizona Gutter Know-How</h3>
            <p className="text-gray-700">We understand how Arizona's climate impacts gutters, delivering a thorough clean that prepares your home for any weather.</p>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto">
        <div className="bg-[#f8f7f3] border-l-4 border-[#b87c4c] p-6 rounded-lg shadow mb-8">
          <h3 className="text-lg font-bold text-[#b87c4c] mb-2">Get a Free Quote for Gutter Cleaning!</h3>
          <p className="mb-4 text-gray-700">Contact us today for a special offer on our gutter cleaning services.</p>
          <Link 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow hover:bg-[#6B3410] transition-colors duration-200"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="max-w-2xl mx-auto mt-12 mb-12">
        <MiniContactForm />
      </div>
      <FAQ
        items={[
          {
            question: 'How often should I have my gutters cleaned?',
            answer: 'We recommend cleaning your gutters at least twice a year, especially in the spring and fall.'
          },
          {
            question: 'Do you remove all debris from the gutters and downspouts?',
            answer: 'Yes, we ensure all debris is removed from both gutters and downspouts for optimal water flow.'
          },
          {
            question: 'Can you clean gutters on multi-story homes?',
            answer: 'Yes, our team is equipped to safely clean gutters on homes of all sizes and heights.'
          }
        ]}
        title="Frequently Asked Questions About Gutter Cleaning"
      />
    </div>
  );
};

export default GutterCleaning; 